"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const questionsToAsk = [{
  number: "01",
  question: "Is this machine new or used?",
  guidance: "Clarify the unit's exact background, previous operating environment (such as commercial venue or rental fleet), and overall operating hours or history."
}, {
  number: "02",
  question: "What exactly is included in the advertised price?",
  guidance: "Confirm whether the quoted package includes the mechanical motion base, bull body, control console, inflatable arena, and air blower, or if specific items are sold separately."
}, {
  number: "03",
  question: "Which controller comes with the machine?",
  guidance: "Identify whether the control console is manual analog, digital touchscreen, or equipped with preset automatic ride profiles and safety stop controls."
}, {
  number: "04",
  question: "Is an inflatable safety arena included?",
  guidance: "Verify the presence, dimensions, perimeter wall configuration, and structural condition of the inflatable safety mattress."
}, {
  number: "05",
  question: "Is a blower included?",
  guidance: "Confirm whether an appropriately sized commercial inflation blower is included with the arena or needs to be procured separately."
}, {
  number: "06",
  question: "What are the exact power requirements?",
  guidance: "Request the required voltage, phase, running amperage, and dedicated circuit ratings for both the mechanical bull base and the inflation blower."
}, {
  number: "07",
  question: "What is the complete operating footprint?",
  guidance: "Check total required clear space including the inflated arena diameter, spectator buffer perimeter, operator console setback, and minimum ceiling height."
}, {
  number: "08",
  question: "What condition information is available?",
  guidance: "For pre-owned machinery, request current operational video, clear photos of mechanical and cosmetic wear points, and recent maintenance logs."
}, {
  number: "09",
  question: "Are replacement parts available?",
  guidance: "Confirm whether critical wear items—such as drive belts, switches, seals, padding, and body skins—can be readily sourced when maintenance is needed."
}, {
  number: "10",
  question: "What warranty or support applies, if any?",
  guidance: "Review what manufacturer or seller warranty coverage is included, what components are covered, and what direct technical assistance is provided."
}, {
  number: "11",
  question: "How will the machine be shipped?",
  guidance: "Ask about freight carrier arrangements, palletized crate dimensions, freight classification, total shipping weight, and whether terminal pickup is needed."
}, {
  number: "12",
  question: "What unloading or access requirements should I plan for?",
  guidance: "Determine if liftgate freight service is feasible or if a commercial loading dock, forklift, or pallet jack is required at your delivery location."
}];
export default function Checklist() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="what-to-check-before-you-buy" data-nav="light" className="border-t border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        {/* Header Block */}
        <div className="mb-14 border-b border-border pb-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <span className="font-mono text-sm tracking-widest text-primary uppercase">
                Buyer Diligence Checklist
              </span>
              <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl md:text-5xl">
                Questions to Ask Before Buying a Mechanical Bull
              </h2>
              <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
                Before purchasing a mechanical bull, ask the seller these twelve essential questions to clarify equipment condition, electrical needs, included components, and logistics.
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <Button asChild variant="outline" className="rounded-lg border-foreground text-foreground hover:bg-foreground hover:text-background">
                <Link to="/ContactPage">
                  Ask a Question
                </Link>
              </Button>
              <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/MechanicalBullPriceGuidePage">
                  Price Guide
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* 12-Item Questions Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {questionsToAsk.map((item, i) => <motion.div key={item.number} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.35,
          delay: shouldReduceMotion ? 0 : i % 3 * 0.08
        }} className="flex flex-col justify-between border border-border bg-card p-6 md:p-7">
              <div>
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <span className="font-mono text-xs font-semibold tracking-wider text-primary">
                    ITEM {item.number}
                  </span>
                  <div className="flex size-7 items-center justify-center border border-border bg-muted">
                    <HelpCircle className="size-3.5 text-muted-foreground" />
                  </div>
                </div>

                <h3 className="mt-4 font-serif text-xl font-bold tracking-tight text-foreground uppercase">
                  {item.question}
                </h3>

                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground">
                  {item.guidance}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* Closing Diligence Notice */}
        <div className="mt-12 border border-border bg-muted p-6 md:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-4xl font-sans text-base font-medium leading-relaxed text-foreground">
              If a seller cannot answer an important product-specific question, treat that uncertainty as part of the buying decision rather than filling the gap with an assumption.
            </p>
            <div className="shrink-0">
              <Button asChild variant="outline" className="rounded-lg border-foreground text-foreground hover:bg-foreground hover:text-background">
                <Link to="/ContactPage">
                  Contact Equipment Team
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}